import React from "react";
import { ArrowLeft } from "lucide-react";

import { getSingleAssetComparisonSeries } from "../../api/client";
import { AssetPerformanceChart } from "../../components/AssetPerformanceChart";
import { AssetTagEditor } from "../asset-overview/AssetTagEditor";
import { decimalMax4, money, percent, ratio } from "../../utils/format";
import { quantityUnit } from "../../utils/assetType";
import {
  chunkInstrumentIds,
  manualComparisonColor,
  mergeMarketSeriesPayloads,
} from "../../utils/manualComparison";
import { calculationBenchmarkSeries, resultPriceSeries } from "../../utils/resultMarketSeries";
import { ChartSelect } from "./ChartSelect";
import { SingleAssetComparisonPicker } from "./SingleAssetComparisonPicker";
import resultStyles from "./singleAssetResult.module.css";
import responsiveStyles from "./singleAssetResponsive.module.css";
import chartStyles from "./singleAssetPerformance.module.css";

// 空值占位符全站统一用长破折号。原来 display() 给 "—"、quoteNumber()/peDisplay() 给 "--"，
// 同一排数字里两种符号混着出现（2026-09-01 截图实测：流通市值 — 挨着 PE --）。
const EMPTY = "—";

// 标的名字长就缩字号。「易方达蓝筹精选混合」在 31px 下放不进标题位，直接省略成
// 「易方达蓝筹精…」等于把最要紧的几个字扔了；缩到 21px 还放不下才轮到省略号
// （2026-09-03 用户发现）。
function titleScaleClass(name) {
  const width = String(name || "").length;
  if (width > 12) return resultStyles.titleTiny;
  if (width > 8) return resultStyles.titleSmall;
  return undefined;
}

function display(value) {
  return value === null || value === undefined || value === "" ? EMPTY : String(value);
}

function toneClass(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric === 0) return "";
  return numeric > 0 ? resultStyles.gain : resultStyles.loss;
}

function signedMoney(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return money(value);
  return numeric > 0 ? `+${money(value)}` : money(value);
}

function signedPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return percent(value);
  return numeric > 0 ? `+${percent(value)}` : percent(value);
}

function quoteNumber(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return EMPTY;
  return numeric.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

function yuanNumber(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return numeric.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function signedQuoteNumber(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return EMPTY;
  return `${numeric > 0 ? "+" : ""}${quoteNumber(numeric)}`;
}

function percentagePoint(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  const sign = numeric > 0 ? "+" : "";
  return `${sign}${numeric.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function compactDateTime(value) {
  if (!value) return "—";
  const normalized = String(value).replace("T", " ").replace(/\+08:00$/, "");
  return normalized.length >= 16 ? normalized.slice(0, 16) : normalized;
}

function Metric({ label, value, hint, tone, primary = false }) {
  return (
    <div className={`${resultStyles.accountingMetric} ${primary ? resultStyles.summaryPrimary : ""}`}>
      <span className={resultStyles.metricLabel}>{label}</span>
      <strong className={tone}>{value}</strong>
      <small>{hint}</small>
    </div>
  );
}

function LiveQuote({ result, liveContext, loading, marketMode }) {
  const isOpenFund = String(result.asset_code || "").toUpperCase().endsWith(".OF");
  const valuation = result.valuation_price_context;
  const metrics = result.metrics || {};
  if (isOpenFund && marketMode) {
    return (
      <div className={resultStyles.marketQuote} aria-label="最新单位净值">
        <span className={resultStyles.quoteLabel}>{compactDateTime(valuation?.price_date ?? result.price_date ?? result.data_date)}</span>
        <div className={resultStyles.quoteValue}>
          <strong>{quoteNumber(valuation?.price)}</strong>
        </div>
        <span className={resultStyles.quoteDate}>单位净值</span>
      </div>
    );
  }
  const realtimeQuote = marketMode
    ? liveContext?.data?.quote
    : valuation?.price_mode === "realtime" ? valuation : null;
  const quoteDate = String(realtimeQuote?.source_as_of_ts || "").slice(0, 10);
  const shanghaiNow = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Shanghai", weekday: "short", year: "numeric", month: "2-digit",
    day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date()).reduce((parts, part) => ({ ...parts, [part.type]: part.value }), {});
  const shanghaiDate = `${shanghaiNow.year}-${shanghaiNow.month}-${shanghaiNow.day}`;
  const shanghaiMinutes = Number(shanghaiNow.hour) * 60 + Number(shanghaiNow.minute);
  const closingQuote = marketMode && realtimeQuote && (
    quoteDate !== shanghaiDate
    || ["Sat", "Sun"].includes(shanghaiNow.weekday)
    || shanghaiMinutes < 570
    || shanghaiMinutes >= 900
  );
  if (realtimeQuote?.price) {
    return (
      <div className={resultStyles.marketQuote} aria-label="实时行情价格">
        <span className={resultStyles.quoteLabel}>{compactDateTime(realtimeQuote.source_as_of_ts)}</span>
        <div className={resultStyles.quoteValue}>
          <strong>{quoteNumber(realtimeQuote.price)}</strong>
          {!closingQuote && <span className={toneClass(realtimeQuote.change)}>{signedQuoteNumber(realtimeQuote.change)}</span>}
          {!closingQuote && <span className={toneClass(realtimeQuote.pct_chg)}>{percentagePoint(realtimeQuote.pct_chg)}</span>}
        </div>
        {closingQuote && <span className={resultStyles.quoteDate}>收盘价</span>}
      </div>
    );
  }
  if (marketMode) {
    return (
      <div className={resultStyles.marketQuote} aria-label="实时行情价格">
        <span className={resultStyles.quoteLabel}>实时行情</span>
        <div className={resultStyles.quoteValue}><strong>{loading ? "读取中…" : EMPTY}</strong></div>
        <span className={resultStyles.quoteDate}>{loading ? "正在读取" : "暂不可用"}</span>
      </div>
    );
  }
  return (
    <div className={resultStyles.marketQuote} aria-label={isOpenFund ? "最新核算净值" : "收盘核算价格"}>
      <span className={resultStyles.quoteLabel}>{compactDateTime(valuation?.source_as_of_ts ?? valuation?.price_date ?? result.price_date ?? result.data_date)}</span>
      <div className={resultStyles.quoteValue}>
        <strong>{loading && !valuation ? "读取中…" : quoteNumber(valuation?.price ?? metrics.latest_price)}</strong>
      </div>
      <span className={resultStyles.quoteDate}>
        {isOpenFund
          ? "单位净值"
          : "收盘价"}
      </span>
    </div>
  );
}

function compactMarketValue(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return `${(numeric / 100000000).toLocaleString("zh-CN", { maximumFractionDigits: 0 })} 亿`;
}

function compactAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return `${(numeric / 100000000).toLocaleString("zh-CN", { maximumFractionDigits: 2 })} 亿`;
}

function peDisplay(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return EMPTY;
  return quoteNumber(numeric);
}

function MarketFacts({ result, liveContext, detail, marketMode }) {
  if (String(result.asset_code || "").toUpperCase().endsWith(".OF")) return null;
  const quote = liveContext?.data?.quote || (marketMode ? {} : detail?.data?.quote) || {};
  const valuation = detail?.data?.valuation || {};
  const facts = [
    ["开盘", quote.open],
    ["最高", quote.high],
    ["最低", quote.low],
    ["成交额", compactAmount(quote.amount ?? detail?.data?.quote?.amount_cny)],
    ["流通市值", compactMarketValue(Number(valuation.circ_mv) * 10000)],
  ];
  return (
    <div className={resultStyles.marketFacts} data-result-market-facts>
      {facts.map(([label, value]) => <div key={label}><span>{label}</span><strong>{display(value)}</strong></div>)}
      <div className={resultStyles.pepb}>
        <span>PE</span><strong>{peDisplay(valuation.pe_ttm)}</strong>
        <span>PB</span><strong>{display(quoteNumber(valuation.pb))}</strong>
      </div>
    </div>
  );
}

// 只有基线（无流水）时，跟踪不满这么多天就先默认看行情窗口而不是「持有期」。
const BASELINE_HOLDING_MIN_DAYS = 60;

function presetStartDate(range, dates, holdingStart) {
  if (!dates.length) return "";
  const last = dates.at(-1);
  if (range === "holding") return holdingStart && holdingStart <= last ? holdingStart : dates[0];
  if (range === "all") return dates[0];
  const end = new Date(`${last}T00:00:00`);
  if (range === "ytd") return `${end.getFullYear()}-01-01`;
  const months = { "1m": 1, "3m": 3, "6m": 6, "1y": 12, "5y": 60 }[range];
  if (!months) return dates[0];
  end.setMonth(end.getMonth() - months);
  return end.toISOString().slice(0, 10);
}

function nearestDateIndex(dates, target, fallback = 0) {
  const index = dates.findIndex((date) => date >= target);
  return index < 0 ? fallback : index;
}

export function PerformanceRangeSelector({ dates, holdingStart, range, setRange, marketMode = false }) {
  const startIndex = nearestDateIndex(dates, range.startDate, 0);
  const endIndex = Math.max(startIndex, nearestDateIndex(dates, range.endDate, dates.length - 1));
  const lastIndex = Math.max(0, dates.length - 1);
  const startPercent = lastIndex ? startIndex / lastIndex * 100 : 0;
  const endPercent = lastIndex ? endIndex / lastIndex * 100 : 100;
  const presets = [
    ...(!marketMode ? [["holding", "持有期"]] : []),
    ["1m", "1月"], ["3m", "3月"], ["6m", "6月"], ["ytd", "年初至今"], ["1y", "1年"], ["5y", "5年"], ["all", "全部"],
  ];
  const applyPreset = (key) => setRange({
    key,
    startDate: presetStartDate(key, dates, holdingStart),
    endDate: dates.at(-1) || "",
  });
  const applyIndex = (kind, rawIndex) => {
    const index = Number(rawIndex);
    const nextStart = kind === "start" ? Math.min(index, endIndex - 1) : startIndex;
    const nextEnd = kind === "end" ? Math.max(index, startIndex + 1) : endIndex;
    setRange({ key: "custom", startDate: dates[Math.max(0, nextStart)], endDate: dates[Math.min(lastIndex, nextEnd)] });
  };
  return (
    <div className={chartStyles.rangePanel}>
      <div className={chartStyles.rangeButtons} aria-label="图表时间范围">
        {presets.map(([key, label]) => (
          <button key={key} type="button" aria-pressed={range.key === key} onClick={() => applyPreset(key)}>{label}</button>
        ))}
        {range.key === "custom" && <span>自定义</span>}
      </div>
      <div className={chartStyles.rangeSlider} aria-label="自定义时间窗口">
        <output aria-label="当前开始日期" style={{ left: `${startPercent}%` }}>{range.startDate}</output>
        <output aria-label="当前结束日期" style={{ left: `${endPercent}%` }}>{range.endDate}</output>
        <span className={chartStyles.rangeTrack} />
        <span className={chartStyles.rangeSelection} style={{ left: `${startPercent}%`, width: `${Math.max(0, endPercent - startPercent)}%` }} />
        <input type="range" min="0" max={lastIndex} value={startIndex} aria-label="开始日期" onChange={(event) => applyIndex("start", event.target.value)} />
        <input type="range" min="0" max={lastIndex} value={endIndex} aria-label="结束日期" onChange={(event) => applyIndex("end", event.target.value)} />
      </div>
    </div>
  );
}

const PerformanceCard = React.memo(function PerformanceCard({ result, marketMode = false }) {
  const isOpenFund = String(result.asset_code || "").toUpperCase().endsWith(".OF");
  const isMoneyMarket = result.asset_subtype === "money_market";
  const suppressMarketBenchmark = !marketMode && isMoneyMarket;
  const baselineOnly = !marketMode
    && Boolean(result.opening_position)
    && (result.transactions || []).length === 0;
  // marketMode（真行情页，没有持仓所以没有盈亏可言）和 baselineOnly（有持仓、有跟踪起点，
  // 只是没有历史流水）以前共用一个开关，于是基线持仓的盈亏曲线和「持有期」范围被一起藏了。
  // 后端本来就按跟踪起点算好了 performance_series，藏起来反而逼用户去点「全部」，
  // 看到行情库第一天起的曲线，更像"他从那天起就持有"。2026-08-31 拆开。
  const eventTimelineOnly = marketMode;
  // 2026-10-29 控制栏改版：价格开关 / 盈亏开关 / K线开关 → 两个下拉。
  // 视图：market = 行情走势（价格，和基准各用一根轴），profit = 持仓盈亏率（全部换成百分比）。
  const [chartView, setChartView] = React.useState(isMoneyMarket ? "profit" : "market");
  const [chartType, setChartType] = React.useState("line");  // line / candle（日 K，不复权）
  const [showMarketBenchmark, setShowMarketBenchmark] = React.useState(true);
  const [compareInstruments, setCompareInstruments] = React.useState([]);
  const [comparisonPayload, setComparisonPayload] = React.useState(null);
  const [comparisonError, setComparisonError] = React.useState("");
  const performance = (eventTimelineOnly ? [] : (result.performance_series || [])).map((point) => ({
    ...point,
    value: Number(point.comparison_growth_multiple ?? point.growth_multiple),
  })).filter((point) => point.date && Number.isFinite(point.value));
  const calculationBenchmark = suppressMarketBenchmark ? [] : calculationBenchmarkSeries(result);
  const tradeDates = (result.transactions || []).map((trade) => trade.trade_date).filter(Boolean);
  const buyDates = (result.transactions || [])
    .filter((trade) => ["BUY", "SUBSCRIBE"].includes(String(trade.action || "").toUpperCase()))
    .map((trade) => trade.trade_date)
    .filter(Boolean);
  // 「持有期」这个范围是"我们从哪天开始能对这笔持仓负责"。有当前持仓基线时
  // 它就是跟踪起点；只有交易时才是首笔买入日。缺了这一支，只有基线的草稿会
  // 回退成"全部行情的第一天"，等于替用户声称他从那天起就持有（§9.3 明确说
  // 实际持有天数不可得）。
  //
  // 基线和流水**可以并存**，而且基线日期未必在流水之前（用户今天才想起来补录
  // 一份持仓，流水却是两年前的）。原来这里用 `||` 短路，有基线就完全不看流水
  // 日期，窗口塌到基线那一天，后端算好的 530 个点只剩最后一个，图直接空掉——
  // 上面的核算 KPI 用的却是 min(所有交易日, 跟踪日)，同一屏两个起点
  // （2026-09-03 用户手动发现，AGENT-093）。两者都在就取更早的那个。
  // 录入持仓可以有多批（2026-09-16），每批一个跟踪起点、一个图上落点
  const openingBatches = result.opening_positions?.length
    ? result.opening_positions
    : (result.opening_position ? [result.opening_position] : []);
  const firstBuyDate = [
    ...openingBatches.map((batch) => batch.tracking_price_date),
    [...(buyDates.length ? buyDates : tradeDates)].sort()[0],
  ].filter(Boolean).sort()[0] || null;
  // 录入的持仓在图上也要有落点：它和买卖一样是一条有日期的事实，
  // 只是没有成交。钉在生效日（用户说的那天），跟踪日只是行情回退的结果。
  const openingPositionEvents = openingBatches
    .filter((batch) => batch.effective_date)
    .map((batch) => ({
      date: batch.effective_date,
      trade_date: batch.effective_date,
      action: "OPENING_POSITION",
      quantity: batch.quantity,
      quantity_unit: batch.quantity_unit,
      price: batch.cost_price,
    }));
  const priceSeries = resultPriceSeries(result);
  const canShowPrice = !isMoneyMarket && priceSeries.length > 0;
  // 没有价格线（货币基金）就只剩盈亏率，视图下拉不显示；行情页没有持仓，只有行情走势
  const showViewSelect = !eventTimelineOnly && canShowPrice;
  const activeView = eventTimelineOnly ? "market" : (canShowPrice ? chartView : "profit");
  const showPrice = canShowPrice;
  const showProfit = activeView === "profit";
  // 日 K：股票 / ETF 有开高低收；场外基金只有净值，没有这个按钮。只在「只看价格」的绝对价格视图下能开。
  const hasCandles = !isOpenFund && !isMoneyMarket && priceSeries.some((point) => Number.isFinite(point.open) && Number.isFinite(point.high) && Number.isFinite(point.low));
  // 默认基准由后端按交易所/板块/品种确定。前端只消费返回的 code/name，
  // 不再按代码后缀猜一次，也不再用 market_observation 快照覆盖这条实时行情。
  const marketBenchmark = suppressMarketBenchmark
    ? null
    : (result.benchmark?.code
      ? {
        instrument_id: `cn.index.${result.benchmark.code}`,
        name: result.benchmark.name || result.benchmark.code,
      }
      : null);
  const excludedComparisonIds = React.useMemo(
    () => [marketBenchmark?.instrument_id].filter(Boolean),
    [marketBenchmark?.instrument_id],
  );
  const excludedComparisonCodes = React.useMemo(
    () => [result.benchmark?.code].filter(Boolean),
    [result.benchmark?.code],
  );

  React.useEffect(() => {
    const manualBatches = chunkInstrumentIds(compareInstruments.map(
      (item) => item.instrument_id,
    ).filter(Boolean));
    if (!manualBatches.length) {
      setComparisonPayload(null);
      return undefined;
    }
    let cancelled = false;
    setComparisonError("");
    const endDate = result.end_date || result.data_date || result.valuation_date;
    void Promise.all(manualBatches.map((batch) => getSingleAssetComparisonSeries({
      instrumentIds: batch,
      endDate,
    }))).then((payloads) => mergeMarketSeriesPayloads(payloads)).then((payload) => {
      if (!cancelled) setComparisonPayload(payload);
    }).catch(() => {
      if (!cancelled) {
        setComparisonPayload(null);
        setComparisonError("暂时取不到对比标的走势。");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [compareInstruments, result.data_date, result.end_date, result.valuation_date]);

  const seriesById = new Map(
    (comparisonPayload?.data?.series || []).map((item) => [item.instrument_id, item]),
  );
  const overlaySeries = [];
  compareInstruments.forEach((instrument) => {
    if (!seriesById.has(instrument.instrument_id)) return;
    overlaySeries.push({
      name: instrument.name,
      color: manualComparisonColor(instrument.instrument_id),
      points: seriesById.get(instrument.instrument_id).points,
    });
  });
  const benchmarkName = String(
    result.benchmark?.name || result.benchmark_series?.[0]?.name || "沪深300",
  );
  const marketBenchmarkSeries = calculationBenchmark;
  const availableDates = [...new Set((eventTimelineOnly
    ? priceSeries.map((point) => point.date)
    : [
      ...priceSeries.map((point) => point.date),
      ...performance.map((point) => point.date),
      ...marketBenchmarkSeries.map((point) => pointDateForResult(point)),
      ...overlaySeries.flatMap((item) => item.points.map((point) => pointDateForResult(point))),
    ]
  ).filter(Boolean))].sort();
  const marketRequestedStart = (marketMode ? result.start_date : "") || availableDates[Math.max(0, availableDates.length - 60)] || "";
  const marketRequestedEnd = result.end_date || availableDates.at(-1) || "";
  // 只有基线时，跟踪起点离最新数据日不足 60 天的话曲线太短没信息量，默认先看
  // 最近 60 个交易日的行情；跟踪够久了再默认「持有期」。有流水的持仓不受影响。
  const trackedDays = baselineOnly && firstBuyDate && availableDates.length
    ? Math.round(
      (new Date(`${availableDates.at(-1)}T00:00:00`) - new Date(`${firstBuyDate}T00:00:00`))
      / 86400000,
    )
    : null;
  const defaultToRequestedWindow = eventTimelineOnly
    || (trackedDays !== null && trackedDays < BASELINE_HOLDING_MIN_DAYS);
  const [range, setRange] = React.useState(() => ({
    key: defaultToRequestedWindow ? "requested" : "holding",
    startDate: defaultToRequestedWindow ? marketRequestedStart : (firstBuyDate || availableDates[0] || ""),
    endDate: defaultToRequestedWindow ? marketRequestedEnd : (availableDates.at(-1) || ""),
  }));
  React.useEffect(() => {
    if (!availableDates.length) return;
    setRange((current) => {
      if (!current.startDate || !current.endDate) return {
        key: defaultToRequestedWindow ? "requested" : "holding",
        startDate: defaultToRequestedWindow ? marketRequestedStart : (firstBuyDate || availableDates[0]),
        endDate: defaultToRequestedWindow ? marketRequestedEnd : availableDates.at(-1),
      };
      if (defaultToRequestedWindow && current.key === "requested") {
        if (current.startDate === marketRequestedStart && current.endDate === marketRequestedEnd) {
          return current;
        }
        return {
          ...current,
          startDate: marketRequestedStart,
          endDate: marketRequestedEnd,
        };
      }
      if (current.key !== "custom") {
        const startDate = presetStartDate(current.key, availableDates, firstBuyDate);
        const endDate = availableDates.at(-1);
        if (current.startDate === startDate && current.endDate === endDate) return current;
        return {
          ...current,
          startDate,
          endDate,
        };
      }
      return current;
    });
  }, [result.asset_code, firstBuyDate, availableDates[0], availableDates.at(-1), defaultToRequestedWindow, marketRequestedStart, marketRequestedEnd]);
  const chartRange = { kind: "custom", startDate: range.startDate, endDate: range.endDate };
  const candleBlocked = compareInstruments.length > 0 ? "取消对比标的后可用" : "";
  const showTypeSelect = hasCandles && activeView === "market";
  const candleActive = showTypeSelect && chartType === "candle" && !candleBlocked;
  const trackingPriceDate = openingBatches.map((batch) => batch.tracking_price_date).filter(Boolean).sort()[0] || null;
  // 后端按 §9.3.2 判定并给出理由，前端不再自己数点：图表区间要等基准序列
  // 异步加载完才定型，前端判会让空态先出现再消失。
  const curveUnavailableReason = (result.unavailable_metrics || [])
    .find((item) => item.metric === "performance_series");
  const curveUnavailable = !eventTimelineOnly && Boolean(curveUnavailableReason);

  return (
    <section className={resultStyles.sectionCard} aria-labelledby="holding-performance-title">
      <div className={resultStyles.sectionHead}>
        <h2 id="holding-performance-title">
          {eventTimelineOnly ? "价格走势" : "持仓表现"}{result.asset_name ? ` - ${result.asset_name}` : ""}
        </h2>
      </div>
      <div className={resultStyles.chartControls}>
        {showViewSelect && (
          <ChartSelect
            label="图表内容"
            value={activeView}
            options={[["market", "行情走势"], ["profit", "持仓盈亏率"]]}
            onChange={setChartView}
          />
        )}
        {showTypeSelect && (
          <ChartSelect
            label="图表类型"
            value={candleActive ? "candle" : "line"}
            options={[["line", "折线图"], ["candle", "K线图", candleBlocked]]}
            onChange={setChartType}
          />
        )}
        <div className={chartStyles.controlsRight}>
          {/* 自动带的基准：选中绿底、不选白底，只开关不删除；手动加的对比标的才有 ×（2026-10-29） */}
          {!suppressMarketBenchmark && (calculationBenchmark.length > 0 || isOpenFund || marketBenchmark) && (
            <button
              className={resultStyles.toggle}
              type="button"
              aria-pressed={showMarketBenchmark}
              onClick={() => setShowMarketBenchmark((current) => !current)}
            >
              {benchmarkName}
            </button>
          )}
          {!isMoneyMarket && (
            <SingleAssetComparisonPicker
              currentInstrumentId={null}
              currentCode={result.asset_code}
              excludedInstrumentIds={excludedComparisonIds}
              excludedCodes={excludedComparisonCodes}
              selected={compareInstruments}
              onChange={setCompareInstruments}
              onError={setComparisonError}
            />
          )}
        </div>
      </div>
      <div className={resultStyles.chartLegend} aria-live="polite">
        {showPrice && <span><i style={{ backgroundColor: candleActive ? "#d63f47" : "#354052" }} />{isOpenFund ? "单位净值" : candleActive ? "日K（不复权）" : "收盘价"}</span>}
        {!eventTimelineOnly && showProfit && <span><i style={{ backgroundColor: "#0b8f5b" }} />持仓盈亏率</span>}
        {showMarketBenchmark && !suppressMarketBenchmark && <span><i className={resultStyles.legendHs300} />{benchmarkName}</span>}
        {!eventTimelineOnly && <span className={chartStyles.eventLegendItem}><b className={chartStyles.eventLegend}>买</b>{isOpenFund ? "申购" : "买入"}</span>}
        {!eventTimelineOnly && <span className={chartStyles.eventLegendItem}><b className={chartStyles.eventLegendSell}>卖</b>{isOpenFund ? "赎回" : "卖出"}</span>}
        {openingPositionEvents.length > 0 && <span className={chartStyles.eventLegendItem}><b className={chartStyles.eventLegendOpening}>持</b>录入持仓</span>}
        <span className={chartStyles.eventLegendItem}><b className={chartStyles.eventLegendDividend}>分</b>股权登记</span>
        <span className={chartStyles.eventLegendItem}><b className={chartStyles.eventLegendShares}>配</b>送转/配股</span>
      </div>
      {comparisonError && <p className={resultStyles.chartMessage}>{comparisonError}</p>}
      {baselineOnly && (
        <p className={resultStyles.chartInfo}>
          {defaultToRequestedWindow
            ? `当前只录入了持仓，跟踪不足 ${BASELINE_HOLDING_MIN_DAYS} 天，先展示近期行情；`
            : "当前只录入了持仓，曲线从跟踪起点开始；"}
          这段走势不代表登记前的持仓收益。
        </p>
      )}
      {curveUnavailable && (
        // §9.3：收益曲线不足两个观察点时不画，§9.3.2 要求给明确空态。行情价格
        // 线不受影响——画不出来的是"这笔持仓赚了多少"，不是这只票的走势。画成
        // 一条平线会被读成"这段时间没赚没亏"，而事实是还没有第二个点。建立当前
        // 持仓的当天必然走到这里（跟踪起点就是当天），是常见路径不是边角情况。
        <p className={resultStyles.chartMessage}>
          {`${curveUnavailableReason?.reason || "还画不出收益曲线。"}`}
          {curveUnavailableReason?.recoverable ? ` ${curveUnavailableReason.recoverable}` : ""}
        </p>
      )}
      <div className={resultStyles.chartWrap}>
        <AssetPerformanceChart
          priceSeries={priceSeries}
          profitSeries={performance}
          benchmarkSeries={showMarketBenchmark ? marketBenchmarkSeries : []}
          additionalSeries={overlaySeries}
          chartRange={chartRange}
          showPrice={showPrice}
          candles={candleActive}
          showProfit={!eventTimelineOnly && showProfit && !curveUnavailable}
          showBenchmark={showMarketBenchmark && !suppressMarketBenchmark}
          assetName={result.asset_name}
          benchmarkName={benchmarkName}
          transactions={eventTimelineOnly ? [] : [...(result.transactions || []), ...openingPositionEvents]}
          assetEvents={result.asset_events || []}
          isFund={isOpenFund}
        />
      </div>
      {availableDates.length > 1 && <PerformanceRangeSelector dates={availableDates} holdingStart={firstBuyDate} range={range} setRange={setRange} marketMode={eventTimelineOnly} />}
    </section>
  );
});

function pointDateForResult(point) {
  return String(point?.date ?? point?.trade_date ?? point?.t ?? "");
}

function assetBadgeLabel(result) {
  const assetType = String(result.asset_type || "").trim();
  const normalized = assetType.toLowerCase();
  if (result.asset_subtype === "money_market") return "货币基金";
  if (result.asset_subtype === "reit") return "REITs";
  if (String(result.asset_code || "").toUpperCase().endsWith(".OF")) return "基金";
  if (normalized === "bond") return "可转债";
  if (["etf", "lof", "fund"].includes(normalized)) return "ETF/LOF";
  return normalized === "stock" || !assetType ? "A股" : assetType;
}

function NewsCard({ liveContext, loading }) {
  const news = liveContext?.data?.news || [];
  const visible = news.slice(0, 4);
  const moreUrl = liveContext?.data?.news_more_url;
  return (
    <aside className={resultStyles.sectionCard} aria-labelledby="related-news-title">
      <div className={resultStyles.sectionHead}>
        <div>
          <h2 id="related-news-title">公司公告</h2>
        </div>
      </div>
      <div className={resultStyles.newsList}>
        {loading && <div className={resultStyles.newsEmpty}>正在读取新浪公司公告…</div>}
        {!loading && visible.map((item) => (
          <a
            className={resultStyles.newsItem}
            href={item.url}
            target="_blank"
            rel="noreferrer"
            key={`${item.published_at}-${item.url}`}
          >
            <strong>{item.title}</strong>
            <span>{item.published_at} · {item.source_name || "新浪公告"}</span>
          </a>
        ))}
        {!loading && news.length === 0 && (
          <div className={resultStyles.newsEmpty}>
            {liveContext?.data?.news_reason || "新浪财经暂未返回该标的的公司公告。"}
          </div>
        )}
      </div>
      {moreUrl && (
        <div className={resultStyles.newsFooter}>
          <a
            className={resultStyles.newsMore}
            href={moreUrl}
            target="_blank"
            rel="noreferrer"
          >
            查看更多 →
          </a>
        </div>
      )}
    </aside>
  );
}

function Fundamentals({ detail }) {
  const fallbackHistory = detail?.data?.financial_history || [];
  const views = detail?.data?.financial_views || { report_period: fallbackHistory, annual: [], single_quarter: [] };
  const [displayMode, setDisplayMode] = React.useState("report_yoy");
  const mode = {
    report_yoy: { viewKey: "report_period", comparisonKey: "yoy" },
    annual_yoy: { viewKey: "annual", comparisonKey: "yoy" },
    quarter_yoy: { viewKey: "single_quarter", comparisonKey: "yoy" },
    quarter_qoq: { viewKey: "single_quarter", comparisonKey: "qoq" },
  }[displayMode];
  const history = (views[mode.viewKey] || []).slice(0, 9);
  React.useEffect(() => {
    setDisplayMode("report_yoy");
  }, [detail?.data?.identity?.ts_code]);
  if (!history.length) return null;
  const items = [
    ["基本每股收益", "basic_eps", " 元"],
    ["每股净资产", "bps", " 元"],
    ["每股资本公积", "capital_reserve_ps", " 元"],
    ["每股未分配利润", "undistributed_profit_ps", " 元"],
    ["每股经营现金流", "ocfps", " 元"],
    ["加权净资产收益率", "weighted_roe", "%"],
    ["销售毛利率", "gross_margin", "%"],
    ["销售净利率", "net_margin", "%"],
    ["资产负债率", "debt_ratio", "%"],
    ["营业总收入", "total_revenue", "amount"],
    ["归母净利润", "parent_net_profit", "amount"],
    ["扣非归母净利润", "deducted_net_profit", "amount"],
  ];
  const periodLabel = (item) => {
    const endDate = String(item.end_date || "");
    const month = endDate.slice(5, 7);
    const label = mode.viewKey === "single_quarter"
      ? month === "03" ? "一季度" : month === "06" ? "二季度" : month === "09" ? "三季度" : "四季度"
      : month === "03" ? "一季报" : month === "06" ? "中报" : month === "09" ? "三季报" : "年报";
    return `${endDate.slice(0, 4)} ${label}`;
  };
  const metricValue = (item, suffix) => {
    if (item?.value === null || item?.value === undefined) return "—";
    if (suffix === "amount") {
      return `${(Number(item.value) / 100000000).toLocaleString("zh-CN", { maximumFractionDigits: 2 })} 亿元`;
    }
    return `${ratio(item.value)}${suffix}`;
  };
  const deltaValue = (value) => {
    if (value === null || value === undefined) return "—";
    const numeric = Number(value);
    return `${numeric > 0 ? "+" : ""}${numeric.toLocaleString("zh-CN", { maximumFractionDigits: 2 })}%`;
  };
  const comparisonTone = (value, label) => {
    if (["扭亏为盈", "亏损收窄"].includes(label)) return resultStyles.gain;
    if (["由盈转亏", "亏损扩大"].includes(label)) return resultStyles.loss;
    return toneClass(value);
  };
  return (
    <section className={`${resultStyles.sectionCard} ${resultStyles.fundamentals}`} aria-labelledby="fundamental-title">
      <div className={resultStyles.sectionHead}>
        <h2 id="fundamental-title">财务指标</h2>
        <label className={resultStyles.financialMode}>
          <span>口径</span>
          <select aria-label="财务数据口径" value={displayMode} onChange={(event) => setDisplayMode(event.target.value)}>
            <option value="report_yoy">报告期同比</option>
            <option value="annual_yoy" disabled={!views.annual?.length}>年度同比</option>
            <option value="quarter_yoy" disabled={!views.single_quarter?.length}>单季度同比</option>
            <option value="quarter_qoq" disabled={!views.single_quarter?.length}>单季度环比</option>
          </select>
        </label>
      </div>
      <div className={resultStyles.financialTableScroll} tabIndex="0" aria-label="财务指标时间序列表，可横向滑动">
        <table className={resultStyles.financialTable}>
          <thead>
            <tr>
              <th scope="col">指标</th>
              {history.map((item) => <th scope="col" key={`${item.end_date}-${item.period_type}`}>{periodLabel(item)}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.map(([label, key, suffix]) => (
              <tr key={key}>
                <th scope="row">{label}</th>
                {history.map((period) => {
                  const metric = period?.metrics?.[key] || {};
                  const comparison = metric[mode.comparisonKey];
                  const semanticLabel = mode.comparisonKey === "qoq" ? metric.qoq_label : null;
                  return (
                    <td key={`${period.end_date}-${key}`}>
                      <strong>{metricValue(metric, suffix)}</strong>
                      <span className={comparisonTone(comparison, semanticLabel)}>
                        {semanticLabel || deltaValue(comparison)}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function SingleAssetPrototypeResult({ result, liveContext, detail, liveLoading, newsLoading, mode = "accounting", headerAction = null, onBack = null, onEditHolding = null }) {
  const marketMode = mode === "market";
  const metrics = result.metrics || {};
  const isMoneyMarket = result.asset_subtype === "money_market";
  const unit = quantityUnit(result.asset_type, result.asset_code);
  const annualized = metrics.money_weighted_annual_return ?? metrics.annualized_return;
  // 只有当前持仓基线、没有完整历史交易时，后端会把算不出的指标置空并在
  // unavailable_metrics 里逐项说明原因。页面照常显示 `--`，但要让用户知道
  // 是事实不足而不是数据坏了。
  const unavailableReason = (metric) =>
    (result.unavailable_metrics || []).find((item) => item.metric === metric)?.reason || undefined;
  // 录入了持仓时，已实现收益和分红只统计录入之后发生的事，但标签与没有录入持仓时
  // 一致，不再加「录入后」前缀（2026-09-20 产品；取代 2026-09-03 的用词修订）。
  const baseline = result.opening_position || null;
  const assetTitleText = result.asset_name || "标的名称待确认";
  const addedQuantity = (result.asset_events || [])
    .filter((event) => ["share_bonus", "rights_issue", "rights_issue_confirmation"].includes(event.event_type))
    .reduce((total, event) => total + Math.max(0, Number(event.quantity_delta || 0)), 0);
  return (
    <div className={`${resultStyles.resultSurface} ${responsiveStyles.responsiveSurface}`}>
      <header className={resultStyles.resultHead} data-result-head>
        <div className={resultStyles.assetIdentity} data-result-identity>
          <div className={resultStyles.assetTitle}>
            {onBack && (
              <button className={resultStyles.resultBackButton} type="button" onClick={onBack} aria-label="返回概览" title="返回概览">
                <ArrowLeft size={18} />
              </button>
            )}
            <h1 title={result.asset_name || "标的名称待确认"}>{result.asset_name || "标的名称待确认"}</h1>
            <div className={resultStyles.assetMetadata}>
              <span className={resultStyles.assetCode}>{result.asset_code}</span>
              <span className={resultStyles.badge}>{assetBadgeLabel(result)}</span>
            </div>
          </div>
          <div className={resultStyles.assetMetaRow}>
            <div className={resultStyles.identityTags}><AssetTagEditor assetCode={result.asset_code} inline /></div>
            {headerAction && <div className={resultStyles.resultHeadAction}>{headerAction}</div>}
          </div>
        </div>
        <LiveQuote result={result} liveContext={liveContext} loading={liveLoading} marketMode={marketMode} />
        <MarketFacts result={result} liveContext={liveContext} detail={detail} marketMode={marketMode} />
      </header>

      <section className={resultStyles.accountingSummary} aria-label="持仓与收益指标" data-result-summary>
        <div className={resultStyles.holdingGroup}>
          <div className={resultStyles.compactMetric}>
            <span>
              当前持仓
              {onEditHolding && (
                <button
                  className={resultStyles.holdingEditButton}
                  type="button"
                  onClick={onEditHolding}
                  aria-label="记录交易"
                  title="记录交易"
                >记录交易</button>
              )}
            </span><strong>{marketMode ? EMPTY : `${decimalMax4(metrics.position_quantity)} ${unit}`}</strong>
            {addedQuantity > 0 && <small>配 {decimalMax4(addedQuantity)}{unit}</small>}
          </div>
          <div className={resultStyles.compactMetric}><span>成本价</span><strong>{marketMode ? EMPTY : `${yuanNumber(metrics.cost_per_share)} 元`}</strong></div>
        </div>
        <div className={resultStyles.profitGroup}>
          <div className={resultStyles.totalProfit}><span>总盈亏</span><strong className={toneClass(metrics.total_profit)}>{signedMoney(metrics.total_profit)}</strong></div>
          <div className={resultStyles.profitBreakdown}>
            <div><span>持仓收益</span><strong className={toneClass(metrics.unrealized_pnl)}>{signedMoney(metrics.unrealized_pnl)}</strong></div>
            <div><span>已实现收益</span><strong className={toneClass(metrics.realized_pnl)}>{signedMoney(metrics.realized_pnl)}</strong></div>
            <div><span>累计分红</span><strong className={toneClass(metrics.dividend_income)}>{signedMoney(metrics.dividend_income)}</strong></div>
          </div>
        </div>
        <div className={resultStyles.returnGroup}>
          <div className={resultStyles.compactMetric} title={unavailableReason("book_total_return")}><span>盈亏比例</span><strong className={toneClass(metrics.book_total_return ?? metrics.total_return)}>{signedPercent(metrics.book_total_return ?? metrics.total_return)}</strong></div>
          <div className={resultStyles.compactMetric} title={unavailableReason("annualized_return") || unavailableReason("money_weighted_annual_return") || (baseline && annualized != null ? "按录入日市值起算" : undefined)}><span>年化收益率</span><strong className={toneClass(annualized)}>{signedPercent(annualized)}</strong></div>
        </div>
      </section>

      <div className={resultStyles.contentGrid}>
        <PerformanceCard
          key={`${result.asset_code}:${mode}`}
          result={result}
          marketMode={marketMode}
        />
        <NewsCard liveContext={liveContext} loading={newsLoading} />
      </div>

      <Fundamentals detail={detail} />

      <div className={resultStyles.resultEvidence}>
        {marketMode
          ? `行情区间 ${display(result.start_date)} 至 ${display(result.end_date)} · ${display(result.trading_day_count)} 个交易日`
          : `核算截止 ${display(result.valuation_date)} · 行情日期 ${display(result.data_date)}`}
      </div>
    </div>
  );
}
